import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { MotionBox, variants } from '../components/motion';
import getVerificationServiceConfig from '../util/verificationService';

const REQUEST_ERROR_MESSAGES = {
  user_not_found: 'We could not find an account for that email or username.',
  rate_limited: 'Too many reset attempts. Please try again later.',
  invalid_redirect: 'The supplied redirect URL is not allowed. Contact support.',
};

const getRequestErrorMessage = (code, fallback) => {
  if (code && REQUEST_ERROR_MESSAGES[code]) {
    return REQUEST_ERROR_MESSAGES[code];
  }
  return fallback || 'Failed to submit password reset request. Please try again.';
};

const parseErrorResponse = async (response) => {
  try {
    const data = await response.json();
    return {
      code: data?.code,
      message: data?.message,
    };
  } catch (err) {
    try {
      const text = await response.text();
      return { code: '', message: text };
    } catch (innerErr) {
      return { code: '', message: '' };
    }
  }
};

export default function ForgotPassword() {
  const { keycloak } = useKeycloak();
  const { resetRequestEndpoint, defaultRedirectUrl } = useMemo(() => getVerificationServiceConfig(), []);
  const [identifier, setIdentifier] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const canSubmit = Boolean(identifier.trim()) && Boolean(resetRequestEndpoint);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!resetRequestEndpoint) {
      setStatus('error');
      setError('Password reset service is not configured. Please contact support.');
      return;
    }

    if (!identifier.trim()) {
      setStatus('error');
      setError('Please enter your email address or username.');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      const payload = { identifier: identifier.trim() };
      if (defaultRedirectUrl) payload.redirectUrl = defaultRedirectUrl;

      const response = await fetch(resetRequestEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const { code, message } = await parseErrorResponse(response);
        throw new Error(getRequestErrorMessage(code, message));
      }

      setStatus('success');

      if (keycloak?.authenticated) {
        await keycloak.logout({ redirectUri: window.location.origin });
      } else {
        await keycloak?.login({ redirectUri: window.location.origin });
      }
    } catch (err) {
      setStatus('error');
      setError(err?.message || 'Failed to submit password reset request. Please try again.');
    }
  }, [defaultRedirectUrl, identifier, keycloak, resetRequestEndpoint]);

  const handleBackToLogin = useCallback(() => {
    keycloak?.login({ redirectUri: window.location.origin });
  }, [keycloak]);

  return (
    <Center minH="100vh" bg="gray.50" px={4}>
      <MotionBox
        as="form"
        onSubmit={handleSubmit}
        maxW="md"
        w="100%"
        p={8}
        borderWidth="1px"
        borderRadius="lg"
        bg="white"
        boxShadow="md"
        variants={variants.item}
        initial="hidden"
        animate="show"
      >
        <VStack spacing={5} align="stretch" textAlign="left">
          <Box textAlign="center">
            <Heading size="lg" mb={2}>Forgot your password?</Heading>
            <Text color="gray.600">
              Enter the email or username associated with your account and we’ll send you a reset link.
            </Text>
          </Box>

          {!resetRequestEndpoint && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Password reset service is not configured. Please contact support.
            </Alert>
          )}

          {status === 'error' && error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl isRequired>
            <FormLabel>Email or username</FormLabel>
            <Input
              type="text"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={status === 'submitting'}
            isDisabled={!canSubmit || status === 'submitting'}
          >
            Send reset link
          </Button>

          {status === 'success' && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              Password reset email sent. Redirecting to login…
            </Alert>
          )}

          <Text fontSize="sm" color="gray.500" textAlign="center">
            Remembered your password?{' '}
            <Button variant="link" colorScheme="blue" onClick={handleBackToLogin}>
              Back to login
            </Button>
          </Text>
        </VStack>
      </MotionBox>
    </Center>
  );
}
